<div class="segy-viewer">
  <!-- ── File picker ───────────────────────────────────────────── -->
  <nz-card nzSize="small" class="picker-card">
    <div class="picker-head">
      <h2 class="picker-title">SEG-Y file viewer</h2>
      <span class="spacer"></span>
      <nz-radio-group
        [ngModel]="isAws()"
        (ngModelChange)="isAws.set($event)"
      >
        <label nz-radio-button [nzValue]="true">
          <span nz-icon nzType="cloud" nzTheme="outline"></span> S3
        </label>
        <label nz-radio-button [nzValue]="false">
          <span nz-icon nzType="desktop" nzTheme="outline"></span> Local
        </label>
      </nz-radio-group>
      <nz-radio-group
        [ngModel]="inputMode()"
        (ngModelChange)="inputMode.set($event)"
        nzButtonStyle="solid"
      >
        <label nz-radio-button nzValue="directory">
          <span nz-icon nzType="folder-open" nzTheme="outline"></span> Browse
        </label>
        <label nz-radio-button nzValue="file">
          <span nz-icon nzType="file" nzTheme="outline"></span> Open file
        </label>
      </nz-radio-group>
      <button
        nz-button
        nz-dropdown
        nzTrigger="click"
        nzPlacement="bottomRight"
        nz-tooltip
        nzTooltipTitle="Files recently opened in this browser"
        [disabled]="!recentFiles().length"
        [nzDropdownMenu]="recentMenu"
      >
        <span nz-icon nzType="history" nzTheme="outline"></span>
        Recent
      </button>
      <nz-dropdown-menu #recentMenu="nzDropdownMenu">
        <ul nz-menu class="recent-menu">
          @for (r of recentFiles(); track r.isAws + ':' + r.path) {
            <li nz-menu-item [title]="r.path" (click)="openRecent(r)">
              <span class="recent-entry">
                <span class="recent-name">{{ fileName(r.path) }}</span>
                <span class="recent-path">{{ r.isAws ? "S3" : "local" }} · {{ r.path }}</span>
              </span>
            </li>
          }
          <li nz-menu-divider></li>
          <li nz-menu-item (click)="clearRecent()">
            <span nz-icon nzType="delete" nzTheme="outline"></span>
            Clear list
          </li>
        </ul>
      </nz-dropdown-menu>
    </div>

    @if (inputMode() === "file") {
      <div class="field-row">
        <span class="field-label">File</span>
        <nz-input-group class="field-control" nzSearch [nzAddOnAfter]="loadBtn">
          <input
            nz-input
            class="path-input"
            [placeholder]="placeholder()"
            [ngModel]="pathInput()"
            (ngModelChange)="pathInput.set($event)"
            (keyup.enter)="load()"
          />
        </nz-input-group>
        <ng-template #loadBtn>
          <button
            nz-button
            nzType="primary"
            nzSearch
            [nzLoading]="infoLoading()"
            [disabled]="!pathInput().trim()"
            (click)="load()"
          >
            <span nz-icon nzType="search"></span>
            Load
          </button>
        </ng-template>
      </div>
    } @else {
      <!-- Bucket first (S3), then the folder within it -->
      @if (isAws() && buckets().length) {
        <div class="field-row">
          <span class="field-label">Bucket</span>
          <nz-select
            class="field-control bucket-select"
            nzShowSearch
            nzPlaceHolder="Select a bucket to browse"
            [ngModel]="currentBucket()"
            (ngModelChange)="$event && browseBucket($event)"
          >
            @for (b of buckets(); track b) {
              <nz-option [nzValue]="b" [nzLabel]="b"></nz-option>
            }
          </nz-select>
        </div>
      }
      <div class="field-row">
        <span class="field-label">Folder</span>
        <nz-input-group class="field-control" nzSearch [nzAddOnAfter]="listBtn">
          <input
            nz-input
            class="path-input"
            [placeholder]="dirPlaceholder()"
            [ngModel]="dirInput()"
            (ngModelChange)="dirInput.set($event)"
            (keyup.enter)="listDir()"
          />
        </nz-input-group>
        <ng-template #listBtn>
          <button
            nz-button
            nzType="primary"
            nzSearch
            [nzLoading]="listLoading()"
            [disabled]="!dirInput().trim()"
            (click)="listDir()"
          >
            <span nz-icon nzType="unordered-list"></span>
            List
          </button>
        </ng-template>
      </div>
    }

    @if (inputMode() === "file" && loadError(); as error) {
      <nz-alert
        class="load-error"
        nzType="error"
        nzShowIcon
        nzMessage="Could not open this file"
        [nzDescription]="error"
      ></nz-alert>
    }

    @if (inputMode() === "directory") {
      @if (listError(); as error) {
        <nz-alert
          class="load-error"
          nzType="error"
          nzShowIcon
          nzMessage="Could not list this directory"
          [nzDescription]="error"
        ></nz-alert>
      } @else if (listing(); as listing) {
        <div class="dir-listing">
          <div class="dir-current" [title]="listing.path">
            <button
              class="crumb-up"
              nz-button
              nzType="text"
              nzSize="small"
              nz-tooltip
              nzTooltipTitle="Up one level"
              [disabled]="!parentDir()"
              (click)="goUp()"
            >
              <span nz-icon nzType="arrow-up" nzTheme="outline"></span>
            </button>
            <span class="crumbs">
              @for (c of dirCrumbs(); track c.path; let last = $last) {
                @if (last) {
                  <span class="crumb current">{{ c.name }}</span>
                } @else {
                  <a class="crumb" (click)="listDir(c.path)">{{ c.name }}</a>
                  <span class="crumb-sep">/</span>
                }
              }
            </span>
            <span class="spacer"></span>
            <span class="muted">
              {{ listing.dirs.length }} folders · {{ listing.files.length }} SEG-Y files
            </span>
          </div>
          @for (d of listing.dirs; track d) {
            <button class="dir-entry" (click)="openDir(d)">
              <span nz-icon nzType="folder" nzTheme="fill" class="folder-icon"></span>
              <span class="file-entry-name">{{ d }}</span>
              <span nz-icon nzType="right" class="muted"></span>
            </button>
          }
          @for (f of listing.files; track f.name) {
            <button class="file-entry" (click)="openFile(f.name)">
              <span nz-icon nzType="file-text" nzTheme="outline"></span>
              <span class="file-entry-name">{{ f.name }}</span>
              <span class="muted">{{ formatBytes(f.size) }}</span>
            </button>
          }
          @if (!listing.dirs.length && !listing.files.length) {
            <span class="muted dir-empty">
              No sub-folders or SEG-Y files in this directory.
            </span>
          }
        </div>
      }
    }
  </nz-card>

  @if (!loaded()) {
    @if (recentFiles().length) {
      <div class="recent-list">
        <div class="recent-head">
          Recently opened
          <span class="spacer"></span>
          <a class="muted" (click)="clearRecent()">clear</a>
        </div>
        @for (r of recentFiles(); track r.isAws + ':' + r.path) {
          <button class="recent-row" [title]="r.path" (click)="openRecent(r)">
            <span nz-icon nzType="file-text" nzTheme="outline"></span>
            <span class="recent-entry">
              <span class="recent-name">{{ fileName(r.path) }}</span>
              <span class="recent-path">{{ r.path }}</span>
            </span>
            <span class="muted">
              {{ r.isAws ? "S3" : "local" }}
              @if (r.size != null) {
                · {{ formatBytes(r.size) }}
              }
            </span>
          </button>
        }
      </div>
    } @else if (inputMode() === "file") {
      <nz-empty
        class="empty-state"
        nzNotFoundContent="Enter the path to a raw .sgy / .segy file and press Load."
      ></nz-empty>
    } @else {
      <nz-empty
        class="empty-state"
        nzNotFoundContent="Pick a bucket or enter a directory and press List, then click a SEG-Y file to open it."
      ></nz-empty>
    }
  } @else if (info(); as info) {
    <!-- ── File summary + custom fields (shared by every tab) ──── -->
    <div class="file-summary">
      <span nz-icon nzType="file-text" nzTheme="outline" class="file-icon"></span>
      <div class="file-text">
        <div class="file-name" [title]="info.path">{{ fileName(info.path) }}</div>
        <div class="file-meta">
          <span>{{ formatBytes(info.size) }}</span>
          @if (info.n_traces != null) {
            <span><b>{{ formatNum(info.n_traces, 0) }}</b> traces</span>
          }
          <span><b>{{ info.ns }}</b> samples @ {{ formatDt(info.dt) }}</span>
          @if (totalTime() != null) {
            <span>{{ formatNum(totalTime(), 3) }} s record</span>
          }
          <span>{{ formatLabel(info.format) }}</span>
          <span>{{ info.is_aws ? "S3" : "local" }}</span>
        </div>
      </div>
      <span class="spacer"></span>
      <button
        nz-button
        nzSize="small"
        class="custom-btn"
        nz-popover
        nzPopoverTrigger="click"
        nzPopoverPlacement="bottomRight"
        nzPopoverTitle="Custom header fields"
        [nzPopoverContent]="customFieldsTpl"
      >
        <span nz-icon nzType="plus-square" nzTheme="outline"></span>
        Custom fields
        @if (customFields().length) {
          <span class="count-pill">{{ customFields().length }}</span>
        }
      </button>
    </div>

    <ng-template #customFieldsTpl>
      <div class="custom-fields-pop">
        <div class="muted">
          Read a non-standard header at any byte. Custom fields appear in every
          header picker and table on this page.
        </div>
        <div class="custom-field-row">
          <nz-input-number
            nzSize="small"
            class="byte-input"
            nzPlaceHolder="byte"
            [nzMin]="1"
            [nzMax]="240"
            [nzStep]="1"
            [ngModel]="customByteDraft()"
            (ngModelChange)="customByteDraft.set($event)"
            (keyup.enter)="addCustomField()"
          ></nz-input-number>
          <nz-select
            nzSize="small"
            class="format-select"
            [ngModel]="customFormatDraft()"
            (ngModelChange)="customFormatDraft.set($event)"
          >
            @for (fmt of headerFormats; track fmt.value) {
              <nz-option [nzValue]="fmt.value" [nzLabel]="fmt.label"></nz-option>
            }
          </nz-select>
          <button nz-button nzSize="small" nzType="primary" (click)="addCustomField()">
            <span nz-icon nzType="plus"></span>
            Add
          </button>
        </div>
        @if (customError(); as err) {
          <div class="error-text">{{ err }}</div>
        }
        @if (customFields().length) {
          <div class="custom-tags">
            @for (f of customFields(); track f.byte + ':' + f.format) {
              <nz-tag nzMode="closeable" (nzOnClose)="removeCustomField(f)">
                byte {{ f.byte }} · {{ f.format }}
              </nz-tag>
            }
          </div>
        }
      </div>
    </ng-template>

    <nz-tabs
      [nzAnimated]="false"
      [nzSelectedIndex]="selectedTab()"
      (nzSelectedIndexChange)="onTabChange($event)"
    >
      <!-- ══ Display: gather + spectrum + gather trace headers ══════ -->
      <nz-tab nzTitle="Display">
        <div class="control-panel display-panel" (keydown.enter)="onPanelEnter($event)">
          <div class="control-rows">
            <!-- Which gather -->
            <div class="gather-bar">
              <span class="group-title">Gather</span>
              <nz-radio-group
                nzSize="small"
                [ngModel]="gatherMode()"
                (ngModelChange)="gatherMode.set($event)"
              >
                <label nz-radio-button nzValue="field">Header value</label>
                <label nz-radio-button nzValue="range">Trace range</label>
              </nz-radio-group>

              @if (gatherMode() === "field") {
                <span class="inline-field">
                  <app-segy-field-select
                    class="group-field"
                    [options]="fieldOptions()"
                    [value]="scanField()"
                    (valueChange)="scanField.set($any($event))"
                  ></app-segy-field-select>
                  <span class="eq">=</span>
                  <nz-input-number
                    nzSize="small"
                    nzPlaceHolder="value"
                    [ngModel]="scanValue()"
                    (ngModelChange)="scanValue.set($event)"
                  ></nz-input-number>
                </span>
              } @else {
                <span class="inline-field">
                  first
                  <nz-input-number
                    nzSize="small"
                    nz-tooltip
                    nzTooltipTitle="0-based index of the first trace"
                    [nzMin]="0"
                    [nzStep]="1"
                    [ngModel]="traceStart()"
                    (ngModelChange)="traceStart.set($event)"
                  ></nz-input-number>
                  count
                  <nz-input-number
                    nzSize="small"
                    [nzMin]="1"
                    [nzStep]="100"
                    [ngModel]="traceCount()"
                    (ngModelChange)="traceCount.set($event)"
                  ></nz-input-number>
                </span>
              }

              <nz-button-group nzSize="small">
                <button
                  nz-button
                  nz-tooltip
                  nzTooltipTitle="Previous gather"
                  [disabled]="!canViewGather()"
                  (click)="stepGather(-1)"
                >
                  <span nz-icon nzType="left"></span>
                </button>
                <button
                  nz-button
                  nz-tooltip
                  nzTooltipTitle="Next gather"
                  [disabled]="!canViewGather()"
                  (click)="stepGather(1)"
                >
                  <span nz-icon nzType="right"></span>
                </button>
              </nz-button-group>
            </div>

            <div class="settings-grid">
              <!-- Time window + amplitude clip share one card -->
              <section class="setting-group">
                <div class="group-head">
                  Frame
                  @if (tMin() != null || tMax() != null) {
                    <a class="group-link" (click)="resetTimeWindow()">full record</a>
                  }
                </div>
                <div class="setting-table frame-table">
                  <span class="row-name">Time</span>
                  <span class="setting-line">
                    <nz-input-number
                      nzSize="small"
                      class="time-input"
                      nzPlaceHolder="0"
                      [nzMin]="0"
                      [nzMax]="totalTime() ?? Infinity"
                      [nzStep]="0.1"
                      [ngModel]="tMin()"
                      (ngModelChange)="tMin.set($event)"
                    ></nz-input-number>
                    <span class="unit">–</span>
                    <nz-input-number
                      nzSize="small"
                      class="time-input"
                      [nzPlaceHolder]="totalTime() != null ? formatNum(totalTime(), 3) : 'end'"
                      [nzMin]="0"
                      [nzMax]="totalTime() ?? Infinity"
                      [nzStep]="0.1"
                      [ngModel]="tMax()"
                      (ngModelChange)="tMax.set($event)"
                    ></nz-input-number>
                    <span class="unit">s</span>
                  </span>

                  <span class="row-name">Clip</span>
                  <span class="setting-line">
                    <nz-radio-group
                      nzSize="small"
                      [ngModel]="clipMode()"
                      (ngModelChange)="clipMode.set($event)"
                    >
                      <label
                        nz-radio-button
                        nzValue="percentile"
                        nz-tooltip
                        nzTooltipTitle="Clip at a percentile of |amplitude| of each gather"
                      >
                        %
                      </label>
                      <label
                        nz-radio-button
                        nzValue="fixed"
                        nz-tooltip
                        nzTooltipTitle="Same ± amplitude for every gather, so they compare directly"
                      >
                        ±
                      </label>
                    </nz-radio-group>
                    @if (clipMode() === "percentile") {
                      <nz-input-number
                        nzSize="small"
                        class="clip-input"
                        [nzMin]="50"
                        [nzMax]="100"
                        [nzStep]="1"
                        [ngModel]="clip()"
                        (ngModelChange)="clip.set($event)"
                      ></nz-input-number>
                      <span class="unit">percentile</span>
                    } @else {
                      <nz-input-number
                        nzSize="small"
                        class="amp-input"
                        nzPlaceHolder="e.g. 1e-3"
                        [nzMin]="0"
                        [ngModel]="ampLimit()"
                        (ngModelChange)="ampLimit.set($event)"
                      ></nz-input-number>
                    }
                  </span>
                </div>
              </section>

              <!-- Bandpass: one line per leg, column headings once -->
              <section class="setting-group">
                <div class="group-head">
                  Bandpass
                  @if (hasFilter()) {
                    <a class="group-link" (click)="lowcut.set(null); highcut.set(null)">clear</a>
                  }
                  @if (nyquist()) {
                    <span class="group-note">Nyquist {{ nyquist() }} Hz</span>
                  }
                </div>
                <div class="setting-table bandpass-table">
                  <span></span>
                  <span class="col-head">Hz</span>
                  <span class="col-head">slope</span>
                  <span class="col-head">phase</span>
                  @for (leg of ["low", "high"]; track leg) {
                    <span class="row-name">{{ leg === "low" ? "Low cut" : "High cut" }}</span>
                    <nz-input-number
                      nzSize="small"
                      class="hz-input"
                      nzPlaceHolder="off"
                      [nzMin]="0"
                      [nzMax]="maxCut()"
                      [nzStep]="1"
                      [ngModel]="leg === 'low' ? lowcut() : highcut()"
                      (ngModelChange)="leg === 'low' ? lowcut.set($event) : highcut.set($event)"
                    ></nz-input-number>
                    <nz-select
                      nzSize="small"
                      class="order-select"
                      [ngModel]="leg === 'low' ? lowRoll() : highRoll()"
                      (ngModelChange)="leg === 'low' ? lowRoll.set($event) : highRoll.set($event)"
                    >
                      @for (r of rolls; track r) {
                        <nz-option [nzValue]="r" [nzLabel]="r + ' dB/oct'"></nz-option>
                      }
                    </nz-select>
                    <nz-radio-group
                      nzSize="small"
                      [ngModel]="leg === 'low' ? lowPhase() : highPhase()"
                      (ngModelChange)="leg === 'low' ? lowPhase.set($event) : highPhase.set($event)"
                    >
                      @for (p of phases; track p.value) {
                        <label nz-radio-button [nzValue]="p.value">{{ p.label }}</label>
                      }
                    </nz-radio-group>
                  }
                </div>
              </section>

              <!-- Mutes: drawn over the gather straight away -->
              <section class="setting-group mute-group" [class.collapsed]="!showMutes()">
                <div class="group-head">
                  <nz-switch
                    nzSize="small"
                    [ngModel]="showMutes()"
                    (ngModelChange)="showMutes.set($event)"
                  ></nz-switch>
                  Mutes
                  <span
                    nz-icon
                    nzType="info-circle"
                    class="info-icon"
                    nz-tooltip
                    nzTooltipTitle="Linear mutes as on the shot page / runfile: t = start + (distance − at offset) / growth. Drawn over the gather straight away — no update needed."
                  ></span>
                  @if (showMutes()) {
                    <a class="group-link" (click)="resetMutes()">defaults</a>
                  }
                </div>
                @if (showMutes()) {
                  <div class="setting-table mute-table">
                    <span></span>
                    <span class="col-head">start (s)</span>
                    <span class="col-head">growth (m/s)</span>
                    <span class="col-head">at offset (m)</span>
                    @for (m of mutes(); track $index; let i = $index) {
                      <label
                        nz-checkbox
                        class="row-name"
                        [class.off]="!m.on"
                        [ngModel]="m.on"
                        (ngModelChange)="setMute(i, 'on', $event)"
                      >
                        <span class="mute-swatch" [style.background]="m.color"></span>{{ m.name }}
                      </label>
                      <nz-input-number
                        nzSize="small"
                        class="hz-input"
                        [nzDisabled]="!m.on"
                        [nzMin]="0"
                        [nzStep]="0.05"
                        [ngModel]="m.start"
                        (ngModelChange)="setMute(i, 'start', $event)"
                      ></nz-input-number>
                      <nz-input-number
                        nzSize="small"
                        class="growth-input"
                        nzPlaceHolder="flat"
                        [nzDisabled]="!m.on"
                        [nzStep]="100"
                        [ngModel]="m.growth"
                        (ngModelChange)="setMute(i, 'growth', $event)"
                      ></nz-input-number>
                      <nz-input-number
                        nzSize="small"
                        class="growth-input"
                        [nzDisabled]="!m.on"
                        [nzStep]="100"
                        [ngModel]="m.atOffset"
                        (ngModelChange)="setMute(i, 'atOffset', $event)"
                      ></nz-input-number>
                    }
                  </div>
                  <div class="setting-line">
                    <span
                      class="unit"
                      nz-tooltip
                      nzTooltipTitle="|offset| header, or the source–receiver distance from the coordinates (x, y) or coordinates + elevations (x, y, z)"
                    >
                      distance
                    </span>
                    <nz-select
                      nzSize="small"
                      class="distance-select"
                      [ngModel]="muteDistance()"
                      (ngModelChange)="muteDistance.set($event)"
                    >
                      <nz-option nzValue="offset" nzLabel="|offset|"></nz-option>
                      <nz-option nzValue="horizontal" nzLabel="x, y"></nz-option>
                      <nz-option nzValue="3d" nzLabel="x, y, z"></nz-option>
                    </nz-select>
                    <nz-radio-group
                      nzSize="small"
                      nz-tooltip
                      [nzTooltipTitle]="
                        'Distance units in this file\'s headers' +
                        (headerUnits() ? ' (binary header says ' + headerUnits() + ')' : ' (not set in the binary header)') +
                        '. Feet are converted, so mute values are always m and m/s.'
                      "
                      [ngModel]="fileUnits()"
                      (ngModelChange)="fileUnits.set($event)"
                    >
                      <label nz-radio-button nzValue="m">m</label>
                      <label nz-radio-button nzValue="ft">ft</label>
                    </nz-radio-group>
                    @if (headerUnits() && headerUnits() !== fileUnits()) {
                      <span class="error-text" nz-tooltip nzTooltipTitle="The binary header says otherwise">≠ header</span>
                    }
                    @if (muteDistance() === "3d") {
                      <span
                        nz-icon
                        nzType="info-circle"
                        class="info-icon"
                        nz-tooltip
                        nzTooltipTitle="z: source = souelev − soudepth, receiver = recelev (× scalel); x, y × scalco"
                      ></span>
                    }
                  </div>
                  <!-- auto pick: scprep's first-break gate (hidden for now) -->
                  @if (autoPickEnabled) {
                  <div class="setting-line auto-line">
                    <button
                      nz-button
                      nzSize="small"
                      nz-tooltip
                      [nzTooltipTitle]="'Pick the first breaks of this gather (as scprep\'s auto gate) and fit a top mute into mute ' + (autoMuteIndex + 1)"
                      [nzLoading]="automuteLoading()"
                      [disabled]="!gather()"
                      (click)="autoPickMutes()"
                    >
                      <span nz-icon nzType="thunderbolt" nzTheme="outline"></span>
                      Auto pick
                    </button>
                    <span class="unit">shift</span>
                    <nz-input-number
                      nzSize="small"
                      class="hz-input"
                      nz-tooltip
                      nzTooltipTitle="Top mute relative to the first breaks (negative = above)"
                      [nzStep]="10"
                      [ngModel]="autoShiftMs()"
                      (ngModelChange)="autoShiftMs.set($event ?? 0)"
                    ></nz-input-number>
                    <span class="unit">ms</span>
                    @if (automute()) {
                      <label
                        nz-checkbox
                        class="unit"
                        nz-tooltip
                        nzTooltipTitle="Also draw the raw per-trace picks (dashed) next to the fitted mute"
                        [ngModel]="showAutoCurves()"
                        (ngModelChange)="showAutoCurves.set($event)"
                      >
                        picks
                      </label>
                    }
                  </div>
                  @if (automuteError(); as err) {
                    <div class="error-text">{{ err }}</div>
                  }
                  }
                  @if (gather() && !muteLines().length && !gatherHeadersLoading() && muteOnCount()) {
                    <div class="error-text">No distances for this gather (headers missing).</div>
                  }
                }
              </section>
            </div>
          </div>
          <!-- applies the gather, limits and filter together -->
          <div class="panel-action">
            <nz-badge [nzDot]="needsUpdate()" [nzOffset]="[-2, 2]">
              <button
                nz-button
                nzType="primary"
                nz-tooltip
                [nzTooltipTitle]="
                  needsUpdate()
                    ? 'Settings changed since this gather was drawn — press to update'
                    : 'Load the gather with these limits and filter'
                "
                [class.needs-update]="needsUpdate()"
                [nzLoading]="gatherLoading()"
                [disabled]="!canViewGather()"
                (click)="viewGather()"
              >
                <span nz-icon [nzType]="needsUpdate() ? 'sync' : 'eye'"></span>
                {{ needsUpdate() ? "Update gather" : "View gather" }}
              </button>
            </nz-badge>
          </div>
        </div>

        @if (gatherMode() === "field" && groupingCapped()) {
          <nz-alert
            class="section-alert"
            nzType="info"
            nzShowIcon
            nzMessage="Very large file — only the first ~5M traces are scanned for grouping"
            nzDescription="Grouping by a header value still works, but the header scan is capped at ~5M traces, so a value that first appears past that window won't be found. To reach it, use “By trace range”: the first trace index is 0-based."
          ></nz-alert>
        }

        @if (gatherError(); as error) {
          <nz-alert
            class="section-alert"
            nzType="warning"
            nzShowIcon
            nzMessage="Could not load gather"
            [nzDescription]="error"
          ></nz-alert>
        }

        @if (gather(); as gather) {
          <div #displayStage class="display-stage" [class.maximized]="maximized()">
            <div class="section-toolbar display-toolbar">
              @if (maximized()) {
                <nz-button-group nzSize="small">
                  <button
                    nz-button
                    nz-tooltip
                    nzTooltipTitle="Previous gather"
                    [disabled]="!canViewGather()"
                    (click)="stepGather(-1)"
                  >
                    <span nz-icon nzType="left"></span>
                  </button>
                  <button
                    nz-button
                    nz-tooltip
                    nzTooltipTitle="Next gather"
                    [disabled]="!canViewGather()"
                    (click)="stepGather(1)"
                  >
                    <span nz-icon nzType="right"></span>
                  </button>
                </nz-button-group>
              }
              <span class="section-title">{{ gatherSummary() }}</span>
              <span class="spacer"></span>
              <span class="tool-group">
                <span
                  class="tool-label"
                  nz-tooltip
                  nzTooltipTitle="Plot header values above the gather, one point per trace, on the gather's own trace axis"
                >
                  Header plot
                </span>
                <app-segy-field-select
                  class="track-select"
                  placeholder="none"
                  [multiple]="true"
                  [maxTagCount]="maxTracks"
                  [options]="fieldOptions()"
                  [value]="trackFields()"
                  (valueChange)="setTrackFields($any($event) ?? [])"
                ></app-segy-field-select>
              </span>
              <span class="tool-sep"></span>
              <span class="tool-group">
                <span class="tool-label">Render</span>
                <nz-radio-group
                  nzSize="small"
                  nz-tooltip
                  nzTooltipTitle="Image: smoothed render · Cells: one block per sample · Wiggle: trace signals (variable area)"
                  [ngModel]="displayMode()"
                  (ngModelChange)="setDisplayMode($event)"
                >
                  <label nz-radio-button nzValue="image">Image</label>
                  <label nz-radio-button nzValue="cells">Cells</label>
                  <label nz-radio-button nzValue="wiggle">Wiggle</label>
                </nz-radio-group>
                @if (displayMode() === "wiggle") {
                  <span
                    class="inline-field"
                    nz-tooltip
                    nzTooltipTitle="Wiggle excursion, in trace spacings at the clip amplitude"
                  >
                    gain
                    <nz-input-number
                      nzSize="small"
                      class="gain-input"
                      [nzMin]="0.1"
                      [nzMax]="10"
                      [nzStep]="0.25"
                      [ngModel]="wiggleGain()"
                      (ngModelChange)="wiggleGain.set($event || 1)"
                    ></nz-input-number>
                  </span>
                  <span
                    class="inline-field"
                    nz-tooltip
                    nzTooltipTitle="Traces skipped between wiggles (0 = draw every trace). Leave empty for auto, which keeps wiggles at least 3 px apart for the current zoom."
                  >
                    skip
                    <nz-input-number
                      nzSize="small"
                      class="gain-input"
                      [nzPlaceHolder]="'auto (' + (wiggleAutoStride() - 1) + ')'"
                      [nzMin]="0"
                      [nzStep]="1"
                      [nzPrecision]="0"
                      [ngModel]="wiggleStep() != null ? wiggleStep()! - 1 : null"
                      (ngModelChange)="wiggleStep.set($event == null || $event === '' ? null : $event + 1)"
                    ></nz-input-number>
                    @if (wiggleStep() != null) {
                      <a class="auto-link" (click)="wiggleStep.set(null)">auto</a>
                    }
                  </span>
                }
              </span>
              @if (hasFilteredGather()) {
                <span class="tool-sep"></span>
                <span class="tool-group">
                  <span class="tool-label">Data</span>
                  <nz-radio-group
                    nzSize="small"
                    nz-tooltip
                    nzTooltipTitle="Flip to compare the bandpass impact"
                    [ngModel]="showRaw()"
                    (ngModelChange)="showRaw.set($event)"
                  >
                    <label nz-radio-button [nzValue]="false">Filtered</label>
                    <label nz-radio-button [nzValue]="true">Raw</label>
                  </nz-radio-group>
                </span>
              }
              <span class="tool-sep"></span>
              <!-- view actions: icon-only, so they read apart from the choices -->
              <nz-button-group nzSize="small" class="view-actions">
                @if (!maximized()) {
                  <button
                    nz-button
                    nz-tooltip
                    [nzTooltipTitle]="showSpectrum() ? 'Hide the spectrum' : 'Show the spectrum'"
                    [class.toggle-on]="showSpectrum()"
                    (click)="showSpectrum.set(!showSpectrum())"
                  >
                    <span nz-icon nzType="bar-chart"></span>
                  </button>
                }
                <button
                  nz-button
                  nz-dropdown
                  nzTrigger="click"
                  nzPlacement="bottomRight"
                  nz-tooltip
                  nzTooltipTitle="Download a PNG"
                  [nzClickHide]="false"
                  [nzVisible]="shotMenuOpen()"
                  (nzVisibleChange)="shotMenuOpen.set($event)"
                  [nzDropdownMenu]="shotMenu"
                >
                  <span nz-icon nzType="camera"></span>
                </button>
                <button
                  nz-button
                  nz-tooltip
                  [nzTooltipTitle]="maximized() ? 'Restore (Esc)' : 'Maximise the gather'"
                  (click)="toggleMaximized()"
                >
                  <span nz-icon [nzType]="maximized() ? 'fullscreen-exit' : 'fullscreen'"></span>
                </button>
              </nz-button-group>
              <nz-dropdown-menu #shotMenu="nzDropdownMenu">
                <ul nz-menu>
                  <li nz-menu-item (click)="screenshotDisplay('segy-gather-capture', 'gather')">
                    <span nz-icon nzType="picture" nzTheme="outline"></span>
                    Gather
                  </li>
                  <li
                    nz-menu-item
                    [nzDisabled]="!spectrumVisible()"
                    (click)="screenshotDisplay('segy-spectrum-capture', 'spectrum')"
                  >
                    <span nz-icon nzType="bar-chart" nzTheme="outline"></span>
                    Spectrum
                  </li>
                  <li
                    nz-menu-item
                    [nzDisabled]="!spectrumVisible()"
                    (click)="screenshotDisplay('segy-display-capture', 'display')"
                  >
                    <span nz-icon nzType="layout" nzTheme="outline"></span>
                    Gather + spectrum
                  </li>
                  <li nz-menu-divider></li>
                  <li
                    nz-menu-item
                    [nzDisabled]="!gatherTracks().length"
                    (click)="screenshotTracks.set(!screenshotTracks())"
                  >
                    <label
                      nz-checkbox
                      class="menu-check"
                      [ngModel]="screenshotTracks() && gatherTracks().length > 0"
                      [nzDisabled]="!gatherTracks().length"
                      (click)="$event.preventDefault()"
                    >
                      Include header plot
                    </label>
                  </li>
                </ul>
              </nz-dropdown-menu>
            </div>

            <div class="display-grid" id="segy-display-capture">
              <div class="display-gather" id="segy-gather-capture">
                <app-segy-gather-viewer
                  [image]="gather.image"
                  [meta]="gather.meta"
                  [loading]="gatherLoading() || (displayMode() !== 'image' && samplesLoading())"
                  [selectedTraceIndex]="selectedTraceOrdinal()"
                  [headerTracks]="displayedTracks()"
                  [mutes]="muteLines()"
                  [displayMode]="displayMode()"
                  [samples]="samples()"
                  [wiggleGain]="wiggleGain()"
                  [wiggleStep]="wiggleStep()"
                  (wiggleStrideChange)="wiggleAutoStride.set($event)"
                  (traceClick)="onTraceClick($event)"
                ></app-segy-gather-viewer>
              </div>
              @if (spectrumVisible()) {
              <div class="display-spectrum" id="segy-spectrum-capture">
                <div class="pane-title">Amplitude spectrum</div>
                @if (spectrumError(); as error) {
                  <nz-alert
                    nzType="warning"
                    nzShowIcon
                    nzMessage="Could not load spectrum"
                    [nzDescription]="error"
                  ></nz-alert>
                } @else {
                  <app-segy-spectrum-chart
                    [spectrum]="spectrum()"
                    [loading]="gatherLoading()"
                    [nyquist]="nyquist()"
                    [limits]="spectrumLimits()"
                    (limitsChange)="spectrumLimits.set($event)"
                  ></app-segy-spectrum-chart>
                }
              </div>
              }
            </div>
          </div>

          <!-- Header values of every trace in this gather -->
          <nz-card #gatherHeadersCard nzSize="small" class="detail-card gather-headers-card">
            <div class="section-toolbar">
              <span class="section-title">Trace headers of this gather</span>
              <span class="muted">click a trace in the gather or a row to mark it</span>
            </div>
            <div class="control-row">
              <span class="row-label">Columns</span>
              <app-segy-field-select
                class="column-select"
                placeholder="choose header fields"
                [multiple]="true"
                [options]="fieldOptions()"
                [value]="gatherColumns()"
                (valueChange)="setGatherColumns($any($event) ?? [])"
              ></app-segy-field-select>
              <button nz-button nzType="link" nzSize="small" (click)="setGatherColumns(defaultColumns())">
                defaults
              </button>
              <button nz-button nzType="link" nzSize="small" (click)="setGatherColumns(allColumns())">
                all fields
              </button>
            </div>

            @if (gatherHeadersError(); as error) {
              <nz-alert
                nzType="error"
                nzShowIcon
                nzMessage="Could not read this gather's trace headers"
                [nzDescription]="error"
              ></nz-alert>
            } @else if (gatherTable(); as table) {
              <nz-table
                #gatherHeaderTable
                nzSize="small"
                [nzData]="table.rows"
                [nzLoading]="gatherHeadersLoading()"
                [nzPageSize]="gatherHeaderPageSize"
                [nzPageIndex]="gatherHeaderPage()"
                (nzPageIndexChange)="gatherHeaderPage.set($event)"
                [nzShowSizeChanger]="false"
                [nzScroll]="{ x: 130 + table.columns.length * 110 + 'px', y: '320px' }"
              >
                <thead>
                  <tr>
                    <th nzLeft nzWidth="60px">#</th>
                    <th nzLeft nzWidth="70px">Trace</th>
                    @for (col of table.columns; track col.name) {
                      <th nzWidth="110px">
                        <div class="th-name">{{ col.label }}</div>
                        @if (col.bytes) {
                          <div class="th-bytes">{{ byteLabel(col.bytes) }}</div>
                        }
                      </th>
                    }
                  </tr>
                </thead>
                <tbody>
                  @for (row of gatherHeaderTable.data; track row.ordinal) {
                    <tr
                      class="clickable-row"
                      [attr.data-ordinal]="row.ordinal"
                      [class.selected-row]="row.ordinal === selectedTraceOrdinal()"
                      (click)="onGatherRowClick(row.ordinal)"
                    >
                      <td nzLeft class="trace-no">{{ row.ordinal + 1 }}</td>
                      <td nzLeft class="trace-no">{{ row.trace }}</td>
                      @for (value of row.values; track $index) {
                        <td>{{ value }}</td>
                      }
                    </tr>
                  }
                </tbody>
              </nz-table>
            } @else if (gatherHeadersLoading()) {
              <div class="section-loading"><nz-spin></nz-spin></div>
            }
          </nz-card>
        } @else if (!gatherLoading()) {
          <nz-empty
            class="tab-empty"
            nzNotFoundContent="Choose a gather (a header value, e.g. ffid = 1001, or a trace range) and press View gather."
          ></nz-empty>
        } @else {
          <div class="section-loading"><nz-spin nzTip="Reading gather..."></nz-spin></div>
        }
      </nz-tab>

      <!-- ══ Geometry & header plots: one sampled header scan ═══════ -->
      <nz-tab nzTitle="Geometry & header plots">
        <div class="control-panel">
          <div class="control-row">
            <span class="row-label">Header scan</span>
            <span
              class="inline-field"
              nz-tooltip
              nzTooltipTitle="How many traces to read, spread evenly across the file. Both the map and the plots below use this one scan."
            >
              sample
              <nz-select
                nzSize="small"
                class="sampling-select"
                [ngModel]="scanPointsValue()"
                (ngModelChange)="setScanPoints($event)"
              >
                @for (p of scanPresets(); track p.value) {
                  <nz-option [nzValue]="p.value" [nzLabel]="p.label"></nz-option>
                }
              </nz-select>
            </span>
            <label
              class="inline-field"
              nz-tooltip
              nzTooltipTitle="Apply the coordinate (scalco) and elevation (scalel) scalars, so positions, elevations and depths are in real units"
            >
              <nz-switch
                nzSize="small"
                [ngModel]="applyScalars()"
                (ngModelChange)="setApplyScalars($event)"
              ></nz-switch>
              real units
            </label>
            <button
              nz-button
              nzSize="small"
              [nzLoading]="scanLoading()"
              (click)="loadScan()"
            >
              <span nz-icon nzType="reload"></span>
              {{ scan() ? "Rescan" : "Scan" }}
            </button>
            @if (scan(); as s) {
              <span class="muted">
                {{ formatNum(s.scanned_traces, 0) }} of {{ formatNum(s.n_traces, 0) }} traces
                @if (s.sampled) {
                  (every {{ s.step }}th)
                }
              </span>
            }
          </div>
        </div>

        @if (scanError(); as error) {
          <nz-alert
            class="section-alert"
            nzType="error"
            nzShowIcon
            nzMessage="Failed to scan the trace headers"
            [nzDescription]="error"
          ></nz-alert>
        }

        @if (scan(); as s) {
          <nz-spin [nzSpinning]="scanLoading()">
            <nz-card nzSize="small" class="detail-card">
              <div class="section-toolbar">
                <span class="section-title">Geometry</span>
                <span class="spacer"></span>
                <button
                  nz-button
                  nzSize="small"
                  nz-tooltip
                  nzTooltipTitle="Download a PNG of this map"
                  (click)="screenshot('segy-geometry-capture', 'geometry')"
                >
                  <span nz-icon nzType="camera"></span>
                </button>
              </div>
              <div id="segy-geometry-capture" class="capture-bg">
                <app-segy-geometry-map
                  [scan]="s"
                  [fieldOptions]="fieldOptions()"
                  [colorField]="geoColor()"
                  (colorFieldChange)="geoColor.set($event)"
                  [layers]="geoLayers()"
                  (layersChange)="geoLayers.set($event)"
                  [box]="gridBox()"
                  (boxChange)="gridBox.set($event)"
                  [highlight]="gatherOnMap()"
                  [showHighlight]="showGatherOnMap()"
                  (showHighlightChange)="showGatherOnMap.set($event)"
                  [fitAngle]="boxFitAngle()"
                  (fitAngleChange)="boxFitAngle.set($event)"
                  [padding]="boxPadding()"
                  (paddingChange)="boxPadding.set($event)"
                ></app-segy-geometry-map>
              </div>
            </nz-card>

            <nz-card nzSize="small" class="detail-card">
              <div class="section-toolbar">
                <span class="section-title">Header plots</span>
                <span class="spacer"></span>
                <button
                  nz-button
                  nzSize="small"
                  nz-tooltip
                  nzTooltipTitle="Download a PNG of this plot"
                  (click)="screenshot('segy-headerplot-capture', 'header-plot')"
                >
                  <span nz-icon nzType="camera"></span>
                </button>
              </div>
              <div id="segy-headerplot-capture" class="capture-bg">
                <app-segy-header-crossplot
                  [scan]="s"
                  [fieldOptions]="fieldOptions()"
                  [xField]="xField()"
                  (xFieldChange)="xField.set($event)"
                  [yField]="yField()"
                  (yFieldChange)="yField.set($event)"
                  [colorField]="xplotColor()"
                  (colorFieldChange)="xplotColor.set($event)"
                ></app-segy-header-crossplot>
              </div>
            </nz-card>
          </nz-spin>
        } @else if (scanLoading()) {
          <div class="section-loading">
            <nz-spin nzTip="Scanning trace headers..."></nz-spin>
          </div>
        }
      </nz-tab>

      <!-- ══ Headers: textual + binary + file-wide trace table ═════ -->
      <nz-tab nzTitle="Headers">
        <nz-card nzSize="small" nzTitle="Trace headers (whole file)" class="detail-card">
          @if (traceHeadersError(); as error) {
            <nz-alert
              nzType="error"
              nzShowIcon
              nzMessage="Failed to load trace headers"
              [nzDescription]="error"
            ></nz-alert>
          } @else {
            <div class="control-row">
              <span class="row-label">Columns</span>
              <app-segy-field-select
                class="column-select"
                placeholder="choose header fields"
                [multiple]="true"
                [options]="fieldOptions()"
                [value]="tableColumns()"
                (valueChange)="tableColumns.set($any($event) ?? [])"
              ></app-segy-field-select>
              <button nz-button nzType="link" nzSize="small" (click)="tableColumns.set(defaultColumns())">
                defaults
              </button>
              <button nz-button nzType="link" nzSize="small" (click)="tableColumns.set(allColumns())">
                all fields
              </button>
            </div>

            <div class="control-row">
              <span class="row-label">
                <span nz-icon nzType="line-chart" nzTheme="outline"></span>
                Plot
              </span>
              <app-segy-field-select
                class="column-select"
                placeholder="header fields to plot"
                [multiple]="true"
                [maxTagCount]="maxTracks"
                [options]="fieldOptions()"
                [value]="tablePlotFields()"
                (valueChange)="tablePlotFields.set(($any($event) ?? []).slice(0, maxTracks))"
              ></app-segy-field-select>
              @if (tablePageRange(); as r) {
                <span class="muted">traces {{ formatNum(r[0], 0) }}–{{ formatNum(r[1], 0) }} (this page)</span>
              }
              @if (tablePlot()?.series?.length) {
                <span class="spacer"></span>
                <button
                  nz-button
                  nzSize="small"
                  nz-tooltip
                  nzTooltipTitle="Download a PNG of this header plot"
                  (click)="screenshot('segy-tableplot-capture', 'header-tracks')"
                >
                  <span nz-icon nzType="camera"></span>
                </button>
              }
            </div>
            @if (tablePlot(); as plot) {
              @if (plot.series.length) {
                <nz-spin [nzSpinning]="traceHeadersLoading()">
                  <app-segy-header-tracks
                    id="segy-tableplot-capture"
                    class="table-plot capture-bg"
                    [traces]="plot.traces"
                    [series]="plot.series"
                  ></app-segy-header-tracks>
                </nz-spin>
              }
            }

            @if (traceTable(); as table) {
              <nz-table
                #traceTableRef
                [nzData]="table.rows"
                nzSize="small"
                [nzFrontPagination]="false"
                [nzTotal]="table.total"
                [nzPageIndex]="tracePage()"
                [nzPageSize]="tracePageSize"
                [nzLoading]="traceHeadersLoading()"
                [nzShowSizeChanger]="false"
                [nzScroll]="{ x: 80 + table.columns.length * 110 + 'px', y: '380px' }"
                (nzPageIndexChange)="onTraceHeaderPageChange($event)"
              >
                <thead>
                  <tr>
                    <th nzLeft nzWidth="80px">Trace</th>
                    @for (col of table.columns; track col.name) {
                      <th nzWidth="110px">
                        <div class="th-name">{{ col.label }}</div>
                        @if (col.bytes) {
                          <div class="th-bytes">{{ byteLabel(col.bytes) }}</div>
                        }
                      </th>
                    }
                  </tr>
                </thead>
                <tbody>
                  @for (row of traceTableRef.data; track row.trace) {
                    <tr>
                      <td nzLeft class="trace-no">{{ row.trace }}</td>
                      @for (value of row.values; track $index) {
                        <td>{{ value }}</td>
                      }
                    </tr>
                  }
                </tbody>
              </nz-table>
            } @else if (traceHeadersLoading()) {
              <div class="section-loading"><nz-spin></nz-spin></div>
            }
          }
        </nz-card>

        <div class="header-pair">
          <nz-card nzSize="small" nzTitle="Text header" class="detail-card text-card">
            @if (headersLoading()) {
              <div class="section-loading"><nz-spin nzTip="Loading headers..."></nz-spin></div>
            } @else if (headersError(); as error) {
              <nz-alert
                nzType="error"
                nzShowIcon
                nzMessage="Failed to load file headers"
                [nzDescription]="error"
              ></nz-alert>
            } @else if (headers(); as headers) {
              <pre class="text-header">{{ headers.text_header }}</pre>
              <div class="muted encoding-note">decoded as {{ headers.encoding }}</div>
            }
          </nz-card>

          @if (headers()) {
            <nz-card nzSize="small" nzTitle="Binary header" class="detail-card binary-card">
              <nz-table
                #binaryTable
                [nzData]="binaryHeaderEntries()"
                nzSize="small"
                [nzFrontPagination]="false"
                [nzShowPagination]="false"
                [nzScroll]="{ y: '420px' }"
              >
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  @for (entry of binaryTable.data; track entry.field) {
                    <tr>
                      <td class="th-name">{{ entry.field }}</td>
                      <td>{{ entry.value }}</td>
                    </tr>
                  }
                </tbody>
              </nz-table>
            </nz-card>
          }
        </div>
      </nz-tab>

      <!-- ══ Model: survey geometry of a 3D SEG-Y volume ═══════════ -->
      <nz-tab nzTitle="Model">
        <div class="control-panel">
          <div class="control-row">
            <span class="row-label">Model geometry</span>
            <button
              nz-button
              nzSize="small"
              [nzLoading]="modelLoading()"
              (click)="loadModelGeometry()"
            >
              <span nz-icon nzType="reload"></span>
              {{ modelGeometry() ? "Rescan" : "Scan" }}
            </button>
            @if (modelGeometry(); as m) {
              <span class="muted">
                {{ formatNum(m.n_traces, 0) }} traces
                @if (m.sampled) {
                  · sampled {{ formatNum(m.scanned_traces, 0) }}
                }
                · coordinates from {{ m.coordinate_source }}
              </span>
              <span class="spacer"></span>
              @if (m.grid?.corners) {
                <button
                  nz-button
                  nzSize="small"
                  nz-tooltip
                  nzTooltipTitle="Copy this grid into the model box on the Geometry tab"
                  (click)="useModelGridAsBox()"
                >
                  <span nz-icon nzType="border-outer"></span>
                  Use as model box
                </button>
              }
              <button
                nz-button
                nzSize="small"
                nz-tooltip
                nzTooltipTitle="Download a PNG of the model footprint"
                (click)="screenshot('segy-model-capture', 'model-geometry')"
              >
                <span nz-icon nzType="camera"></span>
              </button>
            }
          </div>
          @if (modelMessage(); as msg) {
            <div class="muted panel-note">{{ msg }}</div>
          }
        </div>

        @if (modelError(); as error) {
          <nz-alert
            class="section-alert"
            nzType="error"
            nzShowIcon
            nzMessage="Could not derive model geometry"
            [nzDescription]="error"
          ></nz-alert>
        } @else if (modelGeometry(); as m) {
          <div id="segy-model-capture" class="capture-bg">
            <div class="model-layout">
              <nz-card nzSize="small" nzTitle="Survey geometry" class="model-readout">
                @if (m.grid; as grid) {
                  <div class="model-row">
                    <span class="m-name">Rotation</span>
                    <span class="m-value">{{ formatNum(grid.rotation, 2) }}°
                      <span class="muted">cw from north</span></span>
                  </div>
                  @if (grid.origin) {
                    <div class="model-row">
                      <span class="m-name">Origin (X, Y)</span>
                      <span class="m-value">
                        {{ formatNum(grid.origin[0]) }}, {{ formatNum(grid.origin[1]) }}
                      </span>
                    </div>
                  }
                  @if (grid.bin_inline != null) {
                    <div class="model-row">
                      <span class="m-name">Bin (il × xl)</span>
                      <span class="m-value">
                        {{ formatNum(grid.bin_inline, 2) }} ×
                        {{ formatNum(grid.bin_crossline, 2) }}
                      </span>
                    </div>
                  }
                  @if (grid.inline_range) {
                    <div class="model-row">
                      <span class="m-name">Inlines</span>
                      <span class="m-value">
                        {{ grid.inline_range[0] }}–{{ grid.inline_range[1] }}
                        ({{ grid.n_inlines }}, inc {{ grid.inline_increment }})
                      </span>
                    </div>
                    <div class="model-row">
                      <span class="m-name">Crosslines</span>
                      <span class="m-value">
                        {{ grid.crossline_range![0] }}–{{ grid.crossline_range![1] }}
                        ({{ grid.n_crosslines }}, inc {{ grid.crossline_increment }})
                      </span>
                    </div>
                  }
                  @if (grid.inline_azimuth != null) {
                    <div class="model-row">
                      <span class="m-name">Azimuth (il / xl)</span>
                      <span class="m-value">
                        {{ formatNum(grid.inline_azimuth, 1) }}° /
                        {{ formatNum(grid.crossline_azimuth, 1) }}°
                      </span>
                    </div>
                  }
                }
                @if (m.bbox; as b) {
                  <div class="model-row">
                    <span class="m-name">Bounding box X</span>
                    <span class="m-value">{{ formatNum(b.min_x) }} … {{ formatNum(b.max_x) }}</span>
                  </div>
                  <div class="model-row">
                    <span class="m-name">Bounding box Y</span>
                    <span class="m-value">{{ formatNum(b.min_y) }} … {{ formatNum(b.max_y) }}</span>
                  </div>
                  <div class="model-row">
                    <span class="m-name">Extent (W × H)</span>
                    <span class="m-value">{{ formatNum(b.width) }} × {{ formatNum(b.height) }}</span>
                  </div>
                }
                <div class="model-row">
                  <span class="m-name">Depth / time axis</span>
                  <span class="m-value">
                    {{ m.z_axis.n_samples }} samples
                    @if (m.z_axis.total != null) {
                      · total {{ formatNum(m.z_axis.total, 3) }}
                    }
                  </span>
                </div>
                @if (m.grid; as grid) {
                  <div class="model-method muted">{{ grid.method }}</div>
                }
              </nz-card>

              @if (modelMapOptions(); as options) {
                <div echarts [options]="options" class="model-map"></div>
              }
            </div>
          </div>
          @if (!m.has_inline_crossline) {
            <nz-alert
              class="section-alert"
              nzType="info"
              nzShowIcon
              nzMessage="No inline/crossline headers found"
              nzDescription="Bin spacing and the rotated grid corners need populated inline (189–192) / crossline (193–196) headers. Without them the rotation + extent are estimated from the coordinate cloud's principal axes."
            ></nz-alert>
          }
        } @else if (modelLoading()) {
          <div class="section-loading">
            <nz-spin nzTip="Scanning model geometry..."></nz-spin>
          </div>
        }
      </nz-tab>
    </nz-tabs>
  }
</div>
